import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class RetainedEarningsService {

  constructor(private http: HttpClient) { }

  /**
   * Load details - action_code=f8ovay
   * Same params as AngularJS. Optional explicitOverrides: { filing_key?, parent_me_string? } from component so values from AngularJS are used.
   */
  loadDetails(
    baseUrl: string,
    globalService: any,
    filterParams: any = {},
    rowData: any = {},
    explicitOverrides?: { filing_key?: any; parent_me_string?: any }
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=f8ovay`;
    const gp = globalService?.globalParams || {};
    const fp = filterParams || {};
    const params = this.mergePreserveDefined({}, gp, fp);
    params.combination_key = rowData.COMBINATION_KEY;
    params.filing_key = explicitOverrides?.filing_key !== undefined && explicitOverrides?.filing_key !== null
      ? explicitOverrides.filing_key
      : this.firstDefined(rowData.CONSOL_GROUP_KEY, rowData.FILING_KEY, fp.filing_key, gp.filing_key, params.filing_key);
    params.parent_me_string = explicitOverrides?.parent_me_string !== undefined && explicitOverrides?.parent_me_string !== null
      ? explicitOverrides.parent_me_string
      : this.firstDefined(fp.parent_me_string, gp.parent_me_string, params.parent_me_string);
    return this.makeLoadRequest(url, params, globalService);
  }

  /**
   * Load init data (for non-modal) - action_code=ehcu0t
   * Same as AngularJS: explicit params with filing_key and parent_me_string from rowData/filterParams/globalParams.
   */
  loadInit(
    baseUrl: string,
    rowData: any = {},
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=ehcu0t`;
    const gp = globalService?.globalParams || {};
    const fp = filterParams || {};
    const params = {
      combination_key: rowData.COMBINATION_KEY,
      tax_year: rowData.TAX_YEAR,
      scenario: fp.scenario || gp.scenario,
      jcd_key: rowData.JCD_KEY != null ? rowData.JCD_KEY : 250,
      filing_key: this.firstDefined(rowData.CONSOL_GROUP_KEY, rowData.FILING_KEY, fp.filing_key, gp.filing_key),
      parent_me_string: this.firstDefined(fp.parent_me_string, gp.parent_me_string)
    };
    return this.makeLoadRequest(url, params, globalService);
  }

  /**
   * Save - action_code=gpkcx2, process_name=RetainedEarnSave
   * Payload: jsonObj, jsonSettings, newExplanation, merge(globalParams, filterParams)
   */
  saveRetainedEarningsForm(
    baseUrl: string,
    rowData: any,
    newExplanation: string,
    oldExplanation: string,
    username: string,
    globalService: any,
    filterParams: any = {},
    _rowDataContext?: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=gpkcx2`;
    const jsonObj = JSON.stringify(rowData);
    const jsonSettings = '{}';
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({ jsonObj }, params);
    params = _.extend({ jsonSettings }, params);
    params.process_name = 'RetainedEarnSave';
    if (newExplanation === undefined || newExplanation === '') {
      params.newExplanation = oldExplanation;
    } else if (oldExplanation === undefined || oldExplanation === '') {
      params.newExplanation = username + ' \n' + newExplanation;
    } else {
      params.newExplanation = username + ' \n' + newExplanation + ' \n' + oldExplanation;
    }
    return this.makeRequest(url, params, globalService);
  }

  /** Merge objects without overwriting with undefined (so AngularJS values from gp/fp are preserved). */
  private mergePreserveDefined(target: any, ...sources: any[]): any {
    const result = { ...target };
    sources.forEach(src => {
      if (src && typeof src === 'object') {
        Object.keys(src).forEach(key => {
          const v = src[key];
          if (v !== undefined && v !== null) {
            result[key] = v;
          }
        });
      }
    });
    return result;
  }

  /** First argument that is not undefined and not null (so real values from AngularJS are used). */
  private firstDefined(...values: any[]): any {
    for (const v of values) {
      if (v !== undefined && v !== null) {
        return v;
      }
    }
    return undefined;
  }

  /** Load endpoints: GET with query params. Include all keys (like AngularJS); use '' for null/undefined so param is always sent. */
  private makeLoadRequest(url: string, params: any, globalService: any): Observable<any> {
    const requestParams = { ...(globalService?.globalParams || {}), ...params };
    let httpParams = new HttpParams();
    Object.keys(requestParams).forEach(key => {
      const val = requestParams[key];
      const str = (val !== undefined && val !== null) ? String(val) : '';
      httpParams = httpParams.set(key, str);
    });
    return this.http.get<any>(url, { params: httpParams, withCredentials: true }).pipe(
      map(response => response),
      catchError(error => {
        console.error('Error in retained earnings load request:', error);
        throw error;
      })
    );
  }

  /** Save endpoint: POST with form body (application/x-www-form-urlencoded) */
  private makeRequest(url: string, params: any, globalService: any): Observable<any> {
    const requestParams = { ...(globalService?.globalParams || {}), ...params };
    const urlEncoded = new URLSearchParams();
    Object.keys(requestParams).forEach(key => {
      if (requestParams[key] !== undefined && requestParams[key] !== null) {
        urlEncoded.set(key, requestParams[key].toString());
      }
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true }).pipe(
      map(response => response),
      catchError(error => {
        console.error('Error in retained earnings request:', error);
        throw error;
      })
    );
  }
}
